import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Video, StopCircle, Circle, Download, Trash2, Play, Pause, Monitor, User, Upload } from 'lucide-react';
import toast from 'react-hot-toast';

const VideoRecorderEnhanced = ({ onVideoReady, existingVideoUrl }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(existingVideoUrl || null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [landingPageWindow, setLandingPageWindow] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const screenStreamRef = useRef(null);
  const webcamStreamRef = useRef(null);
  const combinedStreamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const videoPreviewRef = useRef(null);

  useEffect(() => {
    return () => {
      stopAllStreams();
      if (landingPageWindow) {
        landingPageWindow.close();
      }
    };
  }, [landingPageWindow]);

  const stopAllStreams = () => {
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach(track => track.stop());
      screenStreamRef.current = null;
    }
    if (webcamStreamRef.current) {
      webcamStreamRef.current.getTracks().forEach(track => track.stop());
      webcamStreamRef.current = null;
    }
  };

  const startRecording = async () => {
    try {
      chunksRef.current = [];

      // 1. Get Screen + Audio
      screenStreamRef.current = await navigator.mediaDevices.getDisplayMedia({
        video: { 
          mediaSource: 'screen',
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        }
      });

      // 2. Get Webcam
      webcamStreamRef.current = await navigator.mediaDevices.getUserMedia({
        video: { 
          width: { ideal: 640 },
          height: { ideal: 480 }
        },
        audio: false // Audio already from screen
      });

      // 3. Open Landing Page in new window
      const currentUrl = window.location.origin;
      const viewHash = window.location.pathname.includes('/quote/view/') 
        ? window.location.pathname.split('/quote/view/')[1] 
        : 'demo-quote';
      
      const newWindow = window.open(
        `${currentUrl}/quote/view/${viewHash}?recording=true`,
        '_blank',
        'width=1920,height=1080'
      );
      
      setLandingPageWindow(newWindow);

      // Wait for window to load
      await new Promise(resolve => setTimeout(resolve, 3000));

      // 4. Combine streams (Screen + Webcam overlay)
      // We'll use Canvas to merge screen + webcam
      const canvas = document.createElement('canvas');
      canvas.width = 1920;
      canvas.height = 1080;
      const ctx = canvas.getContext('2d');

      const screenVideo = document.createElement('video');
      screenVideo.srcObject = screenStreamRef.current;
      screenVideo.play();

      const webcamVideo = document.createElement('video');
      webcamVideo.srcObject = webcamStreamRef.current;
      webcamVideo.play();

      // Canvas stream (screen + webcam overlay)
      const canvasStream = canvas.captureStream(30);
      
      // Add audio from screen
      screenStreamRef.current.getAudioTracks().forEach(track => {
        canvasStream.addTrack(track);
      });

      combinedStreamRef.current = canvasStream;

      // Draw loop (screen + webcam overlay in corner)
      const drawFrame = () => {
        if (!isRecording) return;
        
        // Draw screen
        ctx.drawImage(screenVideo, 0, 0, 1920, 1080);
        
        // Draw webcam in bottom-right corner (240x180)
        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.5)';
        ctx.shadowBlur = 10;
        ctx.drawImage(webcamVideo, 1920 - 260, 1080 - 200, 240, 180);
        ctx.restore();
        
        if (isRecording) {
          requestAnimationFrame(drawFrame);
        }
      };
      
      // Start drawing
      setTimeout(() => drawFrame(), 500);

      // 5. Create MediaRecorder with canvas stream
      const options = { 
        mimeType: 'video/webm;codecs=vp9,opus',
        videoBitsPerSecond: 2500000
      };
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options.mimeType = 'video/webm';
      }

      mediaRecorderRef.current = new MediaRecorder(canvasStream, options);

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        const url = URL.createObjectURL(blob);
        setRecordedBlob(blob);
        setRecordedUrl(url);
        
        const base64 = await blobToBase64(blob);
        if (onVideoReady) {
          onVideoReady(base64);
        }
        
        stopAllStreams();
        clearInterval(timerRef.current);
        
        if (landingPageWindow) {
          landingPageWindow.close();
        }
      };

      screenStreamRef.current?.getVideoTracks()[0]?.addEventListener('ended', () => {
        stopRecording();
      });

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

      toast.success('Aufnahme gestartet - Screen + Webcam + Audio');
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Fehler: ' + error.message);
      stopAllStreams();
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      clearInterval(timerRef.current);
      toast('Aufnahme pausiert');
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      toast('Aufnahme fortgesetzt');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      toast.success('Aufnahme beendet');
    }
  };

  const downloadVideo = () => {
    if (!recordedBlob) return;
    
    const url = URL.createObjectURL(recordedBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `angebot-video-${Date.now()}.webm`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Video heruntergeladen');
  };

  const deleteRecording = () => {
    if (recordedUrl && !existingVideoUrl) {
      URL.revokeObjectURL(recordedUrl);
    }
    setRecordedBlob(null);
    setRecordedUrl(null);
    setRecordingTime(0);
    if (onVideoReady) {
      onVideoReady(null);
    }
    toast('Aufnahme gelöscht');
  };

  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Video className="w-5 h-5" />
          Video-Präsentation (optional)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-slate-600">
          Nehmen Sie ein Video auf, um dem Kunden die Optional- und Alternativ-Positionen zu demonstrieren. 
          Das Video wird auf der Landing Page angezeigt und enthält Screen + Webcam + Audio.
        </p>

        <div className="flex items-center gap-3">
          {!isRecording && !recordedUrl && (
            <Button onClick={startRecording} className="flex-1">
              <Circle className="w-4 h-4 mr-2 fill-red-500 text-red-500" />
              Aufnahme starten
            </Button>
          )}

          {isRecording && (
            <>
              {!isPaused ? (
                <Button onClick={pauseRecording} variant="outline" className="flex-1">
                  <Pause className="w-4 h-4 mr-2" />
                  Pausieren
                </Button>
              ) : (
                <Button onClick={resumeRecording} className="flex-1">
                  <Play className="w-4 h-4 mr-2" />
                  Fortsetzen
                </Button>
              )}
              <Button onClick={stopRecording} variant="destructive" className="flex-1">
                <StopCircle className="w-4 h-4 mr-2" />
                Stoppen
              </Button>
            </>
          )}
        </div>

        {isRecording && (
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-full border border-red-200">
              <Circle className="w-3 h-3 fill-red-500 animate-pulse" />
              <span className="font-mono font-bold">{formatTime(recordingTime)}</span>
            </div>
          </div>
        )}

        {recordedUrl && (
          <div className="space-y-3">
            <video
              ref={videoPreviewRef}
              src={recordedUrl}
              controls
              className="w-full rounded-lg border border-slate-200"
            />
            
            <div className="flex gap-2">
              <Button onClick={downloadVideo} variant="outline" className="flex-1">
                <Download className="w-4 h-4 mr-2" />
                Herunterladen
              </Button>
              <Button onClick={deleteRecording} variant="outline" className="text-red-600">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            
            {recordedBlob && (
              <p className="text-xs text-slate-500">
                Dauer: {formatTime(recordingTime)} | Größe: {(recordedBlob.size / 1024 / 1024).toFixed(2)} MB
              </p>
            )}
            
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800">
                ✓ Video wird automatisch auf der Landing Page angezeigt
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default VideoRecorderEnhanced;
